// PowerPoint presentation integration module
import { logger } from "../../utils/logger";

export async function getPowerPointPresentation(): Promise<string> {
    return new Promise((resolve, reject) => {
        if (typeof PowerPoint === 'undefined') {
            reject(new Error("PowerPoint API not available"));
            return;
        }

        PowerPoint.run(async (context) => {
            try {
                logger.debug(`[PowerPoint] Starting PowerPoint.run execution`);
                
                // --- PHASE 1: Load Slides and Shapes Hierarchy ---
                logger.debug(`[PowerPoint] Phase 1: Loading Slides and Shapes Hierarchy...`);
                const slides = context.presentation.slides;
                
                // We load 'items' (the slides) and 'items/shapes/items/id' (the shapes inside them)
                // This 'Deep Load' tells PowerPoint to fetch the hierarchy in one go.
                // Note: We only load IDs first to keep it lightweight.
                logger.debug(`[PowerPoint] Phase 1: Queuing deep load: slides.load("items/shapes/items/id")...`);
                slides.load("items/shapes/items/id");
                
                logger.debug(`[PowerPoint] Phase 1: Calling context.sync() to fetch hierarchy...`);
                await context.sync();
                logger.debug(`[PowerPoint] Phase 1 Complete. Found ${slides.items.length} slides.`);

                // --- PHASE 2: Load Text Content ---
                logger.debug(`[PowerPoint] Phase 2: Loading Text Content...`);
                
                // Now that 'slides.items' AND 'slide.shapes.items' are accessible,
                // we iterate to queue up the text loads.
                let totalShapes = 0;
                for (let i = 0; i < slides.items.length; i++) {
                    const slide = slides.items[i];
                    logger.debug(`[PowerPoint] Phase 2: Processing slide ${i + 1}/${slides.items.length}`);
                    
                    // We can safely access .items now because we loaded 'items/shapes/items' in Phase 1
                    try {
                        logger.debug(`[PowerPoint] Phase 2: Accessing slide.shapes.items for slide ${i + 1}...`);
                        const shapes = slide.shapes.items;
                        logger.debug(`[PowerPoint] Phase 2: Successfully accessed shapes.items for slide ${i + 1}, count: ${shapes.length}`);
                        totalShapes += shapes.length;
                        
                        let shapeIndex = 0;
                        for (const shape of shapes) {
                            shapeIndex++;
                            logger.debug(`[PowerPoint] Phase 2: Queuing text load for shape ${shapeIndex}/${shapes.length} on slide ${i + 1}...`);
                            // Queue up the text load for every shape
                            // We use the slashes to navigate: textFrame -> textRange -> text
                            try {
                                shape.load("textFrame/textRange/text");
                                logger.debug(`[PowerPoint] Phase 2: Queued text load for shape ${shapeIndex} on slide ${i + 1}`);
                            } catch (e) {
                                logger.warn(`[PowerPoint] Phase 2: Warning queuing text load for shape ${shapeIndex} on slide ${i + 1}:`, e);
                            }
                        }
                        logger.debug(`[PowerPoint] Phase 2: Completed queuing text loads for all shapes on slide ${i + 1}`);
                    } catch (e) {
                        logger.error(`[PowerPoint] Phase 2: ERROR accessing shapes.items for slide ${i + 1}:`, e);
                        logger.error(`[PowerPoint] Phase 2: Error details:`, {
                            errorMessage: e instanceof Error ? e.message : String(e),
                            errorStack: e instanceof Error ? e.stack : undefined,
                            slideIndex: i + 1,
                        });
                        throw e;
                    }
                }

                // Execute all the queued text loads
                logger.debug(`[PowerPoint] Phase 2: All text loads queued for ${totalShapes} total shapes, calling context.sync()...`);
                await context.sync();
                logger.debug(`[PowerPoint] Phase 2 Complete. All text loaded.`);

                // --- PHASE 3: Extract and Format ---
                logger.debug(`[PowerPoint] Phase 3: Extracting and Formatting Text...`);
                const allText: string[] = [];

                for (let i = 0; i < slides.items.length; i++) {
                    const slide = slides.items[i];
                    const slideContent: string[] = [];
                    logger.debug(`[PowerPoint] Phase 3: Processing slide ${i + 1}/${slides.items.length}`);
                    
                    // Safe to access again
                    try {
                        const shapes = slide.shapes.items;
                        logger.debug(`[PowerPoint] Phase 3: Accessing shapes.items for slide ${i + 1}, count: ${shapes.length}`);
                        
                        let shapeIndex = 0;
                        for (const shape of shapes) {
                            shapeIndex++;
                            try {
                                // Check if text exists (some shapes like lines don't have textFrame)
                                logger.debug(`[PowerPoint] Phase 3: Checking textFrame for shape ${shapeIndex}/${shapes.length} on slide ${i + 1}...`);
                                if (shape.textFrame && shape.textFrame.textRange && shape.textFrame.textRange.text) {
                                    const text = shape.textFrame.textRange.text.trim();
                                    logger.debug(`[PowerPoint] Phase 3: Found text for shape ${shapeIndex} on slide ${i + 1}, length: ${text.length}`);
                                    if (text) {
                                        slideContent.push(text);
                                        logger.debug(`[PowerPoint] Phase 3: Added text to slide ${i + 1} content`);
                                    } else {
                                        logger.debug(`[PowerPoint] Phase 3: Text is empty for shape ${shapeIndex} on slide ${i + 1}`);
                                    }
                                } else {
                                    logger.debug(`[PowerPoint] Phase 3: Shape ${shapeIndex} on slide ${i + 1} has no textFrame/textRange/text`);
                                }
                            } catch (e) {
                                // Silently ignore shapes that refused text access
                                logger.debug(`[PowerPoint] Phase 3: Skipping shape ${shapeIndex} on slide ${i + 1} (doesn't support text):`, e);
                            }
                        }
                        logger.debug(`[PowerPoint] Phase 3: Completed processing all shapes for slide ${i + 1}, extracted ${slideContent.length} text items`);
                    } catch (e) {
                        logger.error(`[PowerPoint] Phase 3: ERROR accessing shapes.items for slide ${i + 1}:`, e);
                        logger.error(`[PowerPoint] Phase 3: Error details:`, {
                            errorMessage: e instanceof Error ? e.message : String(e),
                            errorStack: e instanceof Error ? e.stack : undefined,
                            slideIndex: i + 1,
                        });
                    }

                    if (slideContent.length > 0) {
                        allText.push(`### Slide ${i + 1}\n${slideContent.join("\n")}`);
                        logger.debug(`[PowerPoint] Phase 3: Added formatted text for slide ${i + 1}`);
                    } else {
                        logger.debug(`[PowerPoint] Phase 3: No text found for slide ${i + 1}`);
                    }
                }

                const finalResult = allText.join("\n\n");
                logger.debug(`[PowerPoint] Total text extracted: ${finalResult.length} characters from ${allText.length} slides`);
                logger.debug(`[PowerPoint] PowerPoint.run execution completed successfully`);
                resolve(finalResult);

            } catch (error) {
                logger.error("[PowerPoint] Fatal Error:", error);
                logger.error("[PowerPoint] Error details:", {
                    errorMessage: error instanceof Error ? error.message : String(error),
                    errorStack: error instanceof Error ? error.stack : undefined,
                    errorName: error instanceof Error ? error.name : typeof error,
                });
                reject(error);
            }
        });
    });
}

/**
 * Parses LLM response into slide data structure
 * Expected format: ### Slide X: Title followed by bullet points
 */
function parseSlidesFromContent(content: string): Array<{ title: string; content: Array<{ text: string; isSubBullet: boolean }> }> {
    const slides: Array<{ title: string; content: Array<{ text: string; isSubBullet: boolean }> }> = [];
    
    // Split content by slide markers (### Slide X:)
    // Using [\s\S] instead of . with 's' flag for compatibility
    const slideRegex = /###\s*Slide\s*\d+:\s*([\s\S]+?)(?=###\s*Slide|$)/g;
    
    let match;
    while ((match = slideRegex.exec(content)) !== null) {
        const slideContent = match[1].trim();
        
        // Split into lines
        const lines = slideContent.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) continue;
        
        // First line is usually the title (or extract from first non-bullet line)
        let title = "";
        const contentItems: Array<{ text: string; isSubBullet: boolean }> = [];
        
        for (const line of lines) {
            const trimmed = line.trim();
            // Skip empty lines
            if (!trimmed) continue;
            
            // Check if it's a sub-bullet (indented with 2+ spaces)
            const isSubBullet = /^\s{2,}/.test(line);
            
            // If it's a bullet point (starts with - or *)
            if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
                // Remove bullet marker and clean up
                let cleanLine = trimmed.replace(/^[-*]\s*/, '').trim();
                // Remove bold markers (**text**) but keep the text
                cleanLine = cleanLine.replace(/\*\*(.+?)\*\*/g, '$1');
                
                if (cleanLine) {
                    contentItems.push({ text: cleanLine, isSubBullet });
                }
            } else if (!title) {
                // First non-bullet line is the title
                title = trimmed.replace(/\*\*(.+?)\*\*/g, '$1');
            } else {
                // Additional non-bullet lines go to content
                const cleanLine = trimmed.replace(/\*\*(.+?)\*\*/g, '$1');
                if (cleanLine) {
                    contentItems.push({ text: cleanLine, isSubBullet: false });
                }
            }
        }
        
        // If no title found, use first content line or default
        if (!title && contentItems.length > 0) {
            title = contentItems[0].text;
            contentItems.shift();
        }
        
        if (title || contentItems.length > 0) {
            slides.push({
                title: title || "New Slide",
                content: contentItems
            });
        }
    }
    
    // If no slides found with regex, try to parse as single slide
    if (slides.length === 0) {
        const lines = content.split('\n').filter(line => line.trim());
        if (lines.length > 0) {
            let title = "";
            const contentItems: Array<{ text: string; isSubBullet: boolean }> = [];
            
            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed) continue;
                
                const isSubBullet = /^\s{2,}/.test(line);
                
                if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
                    const cleanLine = trimmed.replace(/^[-*]\s*/, '').trim().replace(/\*\*(.+?)\*\*/g, '$1');
                    if (cleanLine) contentItems.push({ text: cleanLine, isSubBullet });
                } else if (!title) {
                    title = trimmed.replace(/\*\*(.+?)\*\*/g, '$1');
                } else {
                    const cleanLine = trimmed.replace(/\*\*(.+?)\*\*/g, '$1');
                    if (cleanLine) contentItems.push({ text: cleanLine, isSubBullet: false });
                }
            }
            
            if (title || contentItems.length > 0) {
                slides.push({
                    title: title || "New Slide",
                    content: contentItems
                });
            }
        }
    }
    
    return slides;
}

export async function insertIntoPowerPointSlide(content: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof PowerPoint === 'undefined') {
            reject(new Error("PowerPoint API not available"));
            return;
        }

        logger.debug("[PowerPoint] insert content length:", content.length);

        PowerPoint.run(async (context) => {
            try {
                const slides = context.presentation.slides;
                const slideData = parseSlidesFromContent(content);
                logger.debug(`[PowerPoint] Parsed ${slideData.length} slides`);

                // --- Scenario A: No slides parsed? Insert into current slide ---
                if (slideData.length === 0) {
                    slides.load("items");
                    await context.sync();
                    if (slides.items.length > 0) {
                        const firstSlide = slides.items[0];
                        const textBox = firstSlide.shapes.addTextBox(content);
                        textBox.left = 100; textBox.top = 100;
                        textBox.width = 600; textBox.height = 400;
                        await context.sync();
                        resolve();
                        return;
                    }
                }

                // --- Scenario B: Create New Slides ---
                // 1. Create all slides first
                for (const slideInfo of slideData) {
                    slides.add();
                }
                
                // Sync ONCE to actually create them in PowerPoint
                await context.sync();

                // 2. Reload slides to get the newly created ones
                slides.load("items");
                await context.sync();

                // The new slides are at the end of the items array
                const existingSlideCount = slides.items.length - slideData.length;
                const newSlideRefs: PowerPoint.Slide[] = [];
                for (let i = 0; i < slideData.length; i++) {
                    newSlideRefs.push(slides.items[existingSlideCount + i]);
                }

                // 3. Clean up placeholders on the new slides
                // We can iterate through our 'newSlideRefs' array safely
                for (const slide of newSlideRefs) {
                    slide.shapes.load("items/id");
                }
                await context.sync();

                for (const slide of newSlideRefs) {
                    // Delete all default placeholders (Title/Subtitle)
                    // We iterate backwards to delete safely
                    for (let j = slide.shapes.items.length - 1; j >= 0; j--) {
                        slide.shapes.items[j].delete();
                    }
                }

                // 4. Add Content (Text Boxes)
                for (let i = 0; i < newSlideRefs.length; i++) {
                    const slide = newSlideRefs[i];
                    const info = slideData[i];

                    // Title Box
                    const titleBox = slide.shapes.addTextBox(info.title);
                    titleBox.left = 50; titleBox.top = 50;
                    titleBox.width = 600; titleBox.height = 60;
                    
                    // Format Title immediately (queue the command)
                    titleBox.textFrame.textRange.font.bold = true;
                    titleBox.textFrame.textRange.font.size = 32;

                    // Content Box
                    const lines = info.content.map(item => 
                        item.isSubBullet ? `  • ${item.text}` : `• ${item.text}`
                    );
                    const contentText = lines.join("\n");

                    if (contentText.trim()) {
                        const contentBox = slide.shapes.addTextBox(contentText);
                        contentBox.left = 50; contentBox.top = 120;
                        contentBox.width = 600; contentBox.height = 300;
                        
                        // Format Content immediately
                        contentBox.textFrame.textRange.font.size = 18;
                    }
                }

                // Final Sync to apply all text and formatting
                await context.sync();
                
                logger.debug(`[PowerPoint] Successfully inserted ${slideData.length} slides`);
                resolve();
            } catch (error) {
                logger.error("[PowerPoint] Error inserting content:", error);
                reject(error);
            }
        });
    });
}

// Add more PowerPoint-specific functions as needed
export const powerpointService = {
    getPowerPointPresentation,
    insertIntoPowerPointSlide,
    
    // Add other PowerPoint-specific functions
};


